import { useCallback, useEffect } from "react";

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface ThemeEffect {
    /** Identificador único do tema — usado como data-attribute no span */
    id: string;
    /** Palavras que ativam o tema (case-insensitive) */
    keywords: string[];
    /** CSS injetado para o highlight */
    highlightStyle: string;
    /** Classe CSS aplicada no <span> */
    highlightClass: string;
    /** Função que executa o efeito visual (bolas, confetes, etc.) */
    trigger: (type: "comment" | "post") => void;
}

// ─── Registry de temas ────────────────────────────────────────────────────────

let globalClickHandler: ((themeId: string) => void) | null = null;
const themeRegistry: Map<string, ThemeEffect> = new Map();
let cssInjected = false;

export function registerTheme(theme: ThemeEffect) {
    themeRegistry.set(theme.id, theme);
    injectThemeCss(theme);
}

function injectThemeCss(theme: ThemeEffect) {
    const styleId = `theme-css-${theme.id}`;
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = theme.highlightStyle;
    document.head.appendChild(style);
}

// ─── Delegação de clique global (inicializado uma vez) ───────────────────────

let clickListenerAttached = false;

export function setGlobalThemeClickHandler(handler: (themeId: string) => void) {
    globalClickHandler = handler;
}

export function attachThemeClickListener() {
    if (clickListenerAttached) return;
    clickListenerAttached = true;

    document.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        const themeId = target.dataset?.themeId;
        if (!themeId) return;

        if (globalClickHandler) {
            globalClickHandler(themeId);
        } else {
            // fallback direto caso o hook ainda não tenha montado
            themeRegistry.get(themeId)?.trigger("comment");
        }
    });
}

// ─── Substitui palavras-chave por <span> clicável ────────────────────────────

export function highlightThemeWords(text: string): string {
    if (!text) return text;

    let result = text;

    for (const theme of themeRegistry.values()) {
        if (!theme.keywords.length) continue;

        const pattern = [...theme.keywords]
            .sort((a, b) => b.length - a.length)
            .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
            .join("|");

        if (!pattern) continue;

        const regex = new RegExp(`(${pattern})`, "gi");

        result = result.replace(
            regex,
            `<span class="${theme.highlightClass}" data-theme-id="${theme.id}" style="cursor:pointer">$1</span>`
        );
    }

    return result;
}

/** Detecta se o texto contém palavras de algum tema registrado */
export function hasAnyThemeTrigger(text: string): string | null {
    const lower = text.toLowerCase();
    for (const theme of themeRegistry.values()) {
        if (theme.keywords.some((k) => lower.includes(k))) {
            return theme.id;
        }
    }
    return null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useThemeEffects() {
    const checkAndTrigger = useCallback(
        (text: string, type: "comment" | "post" = "comment") => {
            const themeId = hasAnyThemeTrigger(text);
            if (!themeId) return;
            themeRegistry.get(themeId)?.trigger(type);
        },
        []
    );

    useEffect(() => {
        attachThemeClickListener();

        // clique no span sempre passa pelo checkAndTrigger do hook
        setGlobalThemeClickHandler((themeId) => {
            themeRegistry.get(themeId)?.trigger("comment");
        });

        return () => {
            setGlobalThemeClickHandler(null as any);
        };
    }, [checkAndTrigger]);

    return { checkAndTrigger };
}